// ======================================================================
// 📁 File: MachineAssignmentCard.js
// PURPOSE:
//   Machine selection & assignment (Planning – Drawer)
// ======================================================================

import React, { useState } from "react";

export default function MachineAssignmentCard({
  machineGroups = [],
  machinesByGroup = {},

  // ✅ drawer provides this
  machines = [],

  // ✅ future-safe alias
  selectedMachines,

  selectedMachineId,

  // ✅ refactored API
  onChange,

  // ✅ legacy-safe
  onPrimaryChange,
  onAddMachine,
  onRemoveMachine,
}) {
  const [additionalMachineId, setAdditionalMachineId] = useState("");

  // ------------------------------------------------------------
  // ✅ NORMALIZED MACHINE IDS (CRITICAL FIX)
  // ------------------------------------------------------------
  const machineIds = (
    selectedMachines ?? machines ?? []
  ).map((m) =>
    typeof m === "object" ? m.machine_id : m
  );

  // ------------------------------------------------------------
  // unified emitter
  // ------------------------------------------------------------
  const emitChange = (payload) => {
    if (typeof onChange === "function") {
      onChange(payload);
      return;
    }

    if (
      payload.selectedMachineId !== undefined &&
      typeof onPrimaryChange === "function"
    ) {
      onPrimaryChange(payload.selectedMachineId);
    }

    if (
      Array.isArray(payload.machines) &&
      typeof onAddMachine === "function"
    ) {
      payload.machines.forEach((m) =>
        onAddMachine(m)
      );
    }
  };

  // ------------------------------------------------------------
  // Routing enforcement
  // ------------------------------------------------------------
  const routingGroupId =
    machineGroups?.[0]?.machine_group_id;

  const routingMachines = routingGroupId
    ? machinesByGroup[String(routingGroupId)] || []
    : [];

  const availableAdditionalMachines =
    routingMachines.filter(
      (m) => !machineIds.includes(m.machine_id)
    );

  return (
    <div className="card ppe-card mb-3">
      <div className="card-header">
        Machine Assignment
      </div>

      <div className="card-body row g-3">
        {/* MACHINE GROUP */}
        <div className="col-md-6">
          <label className="form-label">
            Machine Group
          </label>
          <input
            className="form-control"
            readOnly
            value={
              machineGroups?.[0]
                ?.machine_group_name || "—"
            }
          />
        </div>

        {/* PRIMARY MACHINE */}
        <div className="col-md-6">
          <label className="form-label">
            Primary Machine
          </label>

          <select
            className="form-select"
            value={selectedMachineId || ""}
            disabled={!routingGroupId}
            onChange={(e) => {
              const id = Number(e.target.value);

              const updated = id
                ? [
                    id,
                    ...machineIds.filter(
                      (m) => m !== id
                    ),
                  ]
                : [];

              emitChange({
                selectedMachineId: id,
                machines: updated,
              });
            }}
          >
            <option value="">
              Select Machine
            </option>

            {routingMachines.map((m) => (
              <option
                key={m.machine_id}
                value={m.machine_id}
              >
                {m.machine_name}
              </option>
            ))}
          </select>
        </div>

        {/* ADDITIONAL MACHINES */}
        <div className="col-md-12">
          <label className="form-label">
            Additional Machines
          </label>

          {availableAdditionalMachines.length === 0 ? (
            <div className="text-muted small">
              No additional machines available
            </div>
          ) : (
            <div className="d-flex gap-2">
              <select
                className="form-select"
                value={additionalMachineId}
                onChange={(e) =>
                  setAdditionalMachineId(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Select Machine
                </option>

                {availableAdditionalMachines.map(
                  (m) => (
                    <option
                      key={m.machine_id}
                      value={m.machine_id}
                    >
                      {m.machine_name}
                    </option>
                  )
                )}
              </select>

              <button
                className="btn btn-outline-primary"
                disabled={!additionalMachineId}
                onClick={() => {
                  const id = Number(
                    additionalMachineId
                  );

                  emitChange({
                    machines: [...machineIds, id],
                    selectedMachineId,
                  });

                  setAdditionalMachineId("");
                }}
              >
                Add
              </button>
            </div>
          )}

          {/* SELECTED MACHINES LIST */}
          {machineIds.filter(
            (m) => m !== selectedMachineId
          ).length > 0 && (
            <ul className="list-group list-group-flush mt-2">
              {machineIds
                .filter(
                  (m) => m !== selectedMachineId
                )
                .map((mId) => {
                  const machine =
                    routingMachines.find(
                      (rm) =>
                        rm.machine_id === mId
                    );

                  return (
                    <li
                      key={mId}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {machine?.machine_name ||
                        `Machine ${mId}`}

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          emitChange({
                            machines:
                              machineIds.filter(
                                (m) =>
                                  m !== mId
                              ),
                            selectedMachineId:
                              selectedMachineId ===
                              mId
                                ? null
                                : selectedMachineId,
                          })
                        }
                      >
                        ✕
                      </button>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
